/**
 * Studio's copy for one M5 surface (RFC 0006), kept beside strings.ts and
 * spread into it: `strings.releaseOps` and `strings.problems`. Plain data —
 * it imports nothing, so strings.ts stays the one place every screen reads.
 *
 * The surface: an environment's approval requirement (§5.1), the release
 * requests it creates and the decisions on them, and staged rollouts (§5.2).
 */
const n = (count: number, one: string, many: string) => `${count.toLocaleString()} ${count === 1 ? one : many}`;

export const releaseOpsStrings = {
  // ── the approval requirement ─────────────────────────────────────────
  party: {
    role: (role: string) => `${role}s`,
    group: (name: string) => `the group ${name}`,
    member: (name: string) => name,
  },
  needs: (count: number, party: string) => `Needs ${n(count, "approval", "approvals")} from ${party}`,
  noApproval: "No approval needed",
  approvalTitle: "Approval before release",
  approvalLead:
    "With a requirement, every publish or promote into this environment becomes a release request and moves nothing until enough people have approved it. Rollbacks never wait.",
  approvalOff: "No approval: publishing and promoting deploy at once",
  approvalOn: "Require approval",
  approvalCount: "Approvals needed",
  approvalFrom: "From",
  approvalFromRole: "Everyone with a role",
  approvalFromGroup: "A group",
  approvalFromMember: "One member",
  approvalRole: "Role",
  approvalGroup: "Group",
  approvalMember: "Member",
  approvalGroupsFailed: "The groups could not be read, so a group can't be chosen.",
  approvalMembersFailed: "The members could not be read, so a member can't be chosen.",
  distinctFixed: "Never the requester: whoever asked for a release can't approve it, so one person can't publish alone. This can't be switched off.",
  approvalNeedsManage:
    "Changing who must approve is governance, not publishing: it needs the workflows.manage permission (owners and admins). You can still change what ships.",
  approvalBranch: "A branch preview's policy is fixed, so it can't require approval.",
  approvalChoose: "Choose who approves.",
  settingsSaved: (env: string) => `${env}'s settings are saved. They apply from the next publish; the current release keeps serving.`,

  // ── a held publish or promote ────────────────────────────────────────
  heldTitle: "Held for approval",
  held: (env: string, count: number, party: string) =>
    `Held for approval: nothing was deployed to ${env}. ${n(count, "approval", "approvals")} from ${party} are needed, never from you as the requester.`,
  heldStatus: (env: string) => `Held for approval: a release request for ${env} was made; nothing was deployed.`,
  openRequest: "Open the release request",

  // ── the releases page ───────────────────────────────────────────────
  requestsLink: "Release requests",
  pendingCount: (count: number) => `${count.toLocaleString()} pending`,
  envPage: "Approvals & rollout",
  envRequests: "Requests",
  rolloutSummary: (percent: number, v: string) => `Rolling out ${v} to ${percent}%`,

  // ── release requests ────────────────────────────────────────────────
  requestsTitle: "Release requests",
  requestsLead:
    "A publish or promote into an environment that requires approval waits here until enough people other than the requester have approved it. The last approval deploys it.",
  backToReleases: "Releases",
  filterEnvironment: "Environment",
  filterState: "State",
  anyEnvironment: "Every environment",
  anyState: "Every state",
  requestsLoading: "Loading release requests…",
  requestsFailed: "The release requests could not be read, so nothing below is known — not even whether any are waiting.",
  requestsEmpty: "No release requests match. A publish or promote into an environment that requires approval makes one.",
  columns: { release: "Release", environment: "Environment", action: "Change", requester: "Requested by", state: "State", when: "Requested" },
  action: { publish: "Publish", promote: "Promote" } as Record<string, string>,
  state: { pending: "Pending", deployed: "Deployed", denied: "Denied", withdrawn: "Withdrawn", refused: "Refused" } as Record<string, string>,
  stateTone: { pending: "warn", deployed: "ok", denied: "err", withdrawn: "neutral", refused: "err" } as Record<string, string>,
  forcedMarker: "Forced",
  open: "Open",

  // ── one request ─────────────────────────────────────────────────────
  requestTitle: (v: string, env: string) => `${v} to ${env}`,
  requestLoading: "Loading the release request…",
  requestFailed: "The release request could not be read.",
  backToRequests: "All release requests",
  what: (action: string, v: string, env: string) => `${action} ${v} to ${env}`,
  requestedBy: (who: string) => `Requested by ${who}`,
  gate: "Completeness requirement",
  gateMet: "Met when the request was made. It is checked again when the release deploys.",
  gateUnmet: "Not met when the request was made:",
  forced: "Forced: the requester overrode the completeness requirement. Force never bypasses approval — you are deciding on a release that doesn't meet it.",
  forcedReason: (r: string) => `Their reason: ${r}`,
  requirement: "Requirement",
  requirementText: (count: number, party: string) => `${n(count, "approval", "approvals")} from ${party}, never the requester.`,
  approvals: "Approvals",
  progress: (granted: number, required: number) => `${granted.toLocaleString()} of ${n(required, "approval", "approvals")} given`,
  noDecisions: "Nobody has decided yet.",
  approvalLoading: "Loading the approvals…",
  approvalFailed: "The approvals could not be read, so who has decided is unknown.",
  notAskedYet: "The workflow hasn't asked for approvals yet. It does a moment after the request is made.",
  decision: { granted: "Approved", denied: "Denied" } as Record<string, string>,
  by: (who: string) => `by ${who}`,
  reasonText: (r: string) => `“${r}”`,
  closedBy: (state: string, who: string) => `${state} by ${who}`,
  closedReason: (r: string) => `Reason: ${r}`,
  yourDecision: "Your decision",
  reason: "Reason (optional)",
  approve: "Approve release",
  deny: "Deny release",
  approved: (v: string, env: string) => `Approved ${v} to ${env}. It deploys once enough people have approved it.`,
  denied: (v: string, env: string) => `Denied ${v} to ${env}. Nothing was deployed.`,
  ownRequest: "You asked for this release, so someone else must approve it: four-eyes means the requester never counts.",
  cannotDecide: "Approving releases needs the approvals.decide permission (reviewers, admins and owners).",
  alreadyDecided: "You have decided on this request.",
  ownRefusal: "Not yours to approve: you requested this release, and four-eyes means someone other than the requester approves it.",
  notEligible: "Not yours to approve: this release must be approved by other people.",
  personRequired: "Only a person can approve a release: an API token or an agent can't, whatever its scopes.",
  notRequested: "The workflow hasn't asked for approvals yet — it does a moment after the request is made. Try again in a moment.",
  closedNow: "This request was closed meanwhile (deployed, denied, withdrawn or refused). It has been read again.",
  retry: "Read again",
  withdraw: "Withdraw request",
  withdrawTitle: "Withdraw this release request?",
  withdrawLead: "Nothing is deployed and the request closes. A new publish or promote makes a new one.",
  withdrawReason: "Reason (optional)",
  withdrawConfirm: "Withdraw",
  withdrawn: "Withdrawn: the request is closed and nothing was deployed.",
  instances: "Workflow",
  instanceLink: (state: string) => `Workflow instance, now in “${state}”`,

  // ── the environment page ────────────────────────────────────────────
  envTitle: (env: string) => `Environment ${env}`,
  envLoading: "Loading the environment…",
  envFailed: "The environment could not be read.",
  serving: "Serving",
  policy: "Ships",
  approval: "Approval",
  editSettings: "Change settings",
  readOnly: "You can see this environment. Changing its settings and running rollouts need the developer, admin or owner role.",
  // rollouts
  rollout: "Staged rollout",
  rolloutLead:
    "Serve a candidate release to a share of installations, chosen by a stable hash, while the rest keep the release this environment serves. Completing moves the pointer to the candidate; aborting is instant and needs nobody's approval.",
  noActive: "No rollout is running.",
  active: "Running",
  percentOf: (percent: number) => `${percent}% of installations get the candidate`,
  candidate: "Candidate",
  stable: "Everyone else",
  startedBy: (who: string, when: string) => `Started by ${who}, ${when}`,
  expires: (when: string) => `Aborted automatically ${when} unless someone ends it first.`,
  rolloutForced: (reason: string) => `Started past the completeness requirement. Reason: ${reason}`,
  advanceTo: "New share (%)",
  advanceHint: "Any share may follow any other, down as well as up. Installations already in the candidate stay in it at a higher share.",
  advance: "Change share",
  advanced: (percent: number) => `The candidate now goes to ${percent}% of installations.`,
  stale: "Someone changed this rollout meanwhile. It has been read again; check the share and try again.",
  complete: "Complete rollout",
  completeTitle: "Complete the rollout?",
  completeLead: (env: string, v: string) => `This moves ${env}'s pointer to the candidate ${v}: every installation gets it.`,
  completed: (env: string, v: string) => `${env} now serves ${v}.`,
  abort: "Abort rollout",
  abortTitle: "Abort the rollout?",
  abortLead: (env: string, v: string) => `Every installation returns to ${v}, which ${env} serves, on its next refresh.`,
  aborted: "Aborted: every installation returns to the stable release.",
  start: "Start a rollout",
  startRelease: "Candidate release",
  startPercent: "Share of installations (%)",
  startDuration: "Ends by itself after (days)",
  startDurationHint: "Up to 90 days; 14 when left as is. A forgotten rollout is aborted, never left running.",
  startForce: "Start although the completeness requirement isn't met",
  startForceReason: "Why (required with force)",
  startConfirm: "Start rollout",
  started: (v: string, percent: number) => `Rolling out ${v} to ${percent}% of installations.`,
  needsApproval:
    "This environment requires release approvals, and a release request can't carry a rollout yet (RFC 0006 §5.2). Publish or promote through a release request instead.",
  policyNotMet: "The candidate doesn't meet this environment's completeness requirement. Start it anyway only with a reason, which the completing deployment records.",
  history: "Rollout history",
  historyEmpty: "No rollouts yet.",
  historyColumns: { candidate: "Candidate", stable: "Stable", percent: "Share", outcome: "Outcome", started: "Started", ended: "Ended" },
  end: {
    completed: "Completed: the candidate became the release",
    aborted: "Aborted by a person",
    expired: "Expired: aborted at its maximum duration",
    rolled_back: "Ended by a rollback",
  } as Record<string, string>,
  running: "Running",
};

/** Sentences for this surface's problem codes, merged into `strings.problems`. */
export const releaseOpsProblems: Record<string, string> = {
  approval_not_requested: "The workflow hasn't asked for approvals yet. Try again in a moment.",
  release_request_closed: "This release request is closed: it was deployed, denied, withdrawn or refused.",
  invalid_withdraw_reason: "The reason is too long (at most 1,000 characters).",
  approval_on_branch: "A branch preview's policy is fixed, so it can't require approval.",
  rollout_active: "A rollout is already running in this environment. Complete or abort it first.",
  rollout_no_stable: "This environment serves no release yet, so there is nothing to roll out against. Publish or promote first.",
  rollout_candidate_served: "That release is what this environment serves already.",
  rollout_branch_environment: "Branch previews can't run rollouts.",
  rollout_source_locale: "That release has a different source locale from the one this environment serves, so the two can't share one manifest.",
  rollout_needs_approval:
    "This environment requires release approvals, and a release request can't carry a rollout yet. Publish or promote through a release request instead.",
  rollout_ended: "This rollout has ended already.",
  policy_not_met: "The release doesn't meet this environment's completeness requirement.",
  invalid_percent: "The share is a whole number from 0 to 100.",
  invalid_max_duration: "A rollout runs for one hour to 90 days.",
  force_reason_required: "Say why the requirement is set aside. Force without a reason is refused.",
  invalid_force_reason: "A reason goes with force only, and is at most 1,000 characters.",
  branch_release_not_promotable: "A branch preview's release holds text that exists only on the branch, so it can't go anywhere else.",
};
